import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getCostTransactionChartKey } from 'common/constants/queryKeys'
import { getRecentFoodByProductKey } from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { MessageResponse } from 'common/types/api'
import { useInvalidateCostQueries } from 'modules/cost/api/invalidate'

import { FOOD_REQUEST_CONFIG } from '.'
import {
  CreateMealByProductVariables,
  CreateMealVariables,
  CreateProductMessageResponse,
  CreateProductVariables,
  EditMealByProductVariables,
  EditMealVariables,
} from './types'

export const useMutateCreateMeal = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    ({ body: data }: CreateMealVariables) =>
      cycleApi.request<MessageResponse>({ ...FOOD_REQUEST_CONFIG.createMeal, data }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getCostTransactionChartKey.all() })
        await queryClient.invalidateQueries({ queryKey: getRecentFoodByProductKey.all() })
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateCreateMealByProduct = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    ({ body: data }: CreateMealByProductVariables) =>
      cycleApi.request<MessageResponse>({ ...FOOD_REQUEST_CONFIG.createMealByProduct, data }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getCostTransactionChartKey.all() })
        await queryClient.invalidateQueries({ queryKey: getRecentFoodByProductKey.all() })
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateUpdateMeal = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    ({ mealId, body: data }: EditMealVariables) =>
      cycleApi.request<MessageResponse>({
        ...FOOD_REQUEST_CONFIG.editMeal,
        url: `${FOOD_REQUEST_CONFIG.editMeal.url}/${mealId}`,
        data,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getCostTransactionChartKey.all() })
        await queryClient.invalidateQueries({ queryKey: getRecentFoodByProductKey.all() })
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateUpdateMealByProduct = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    ({ mealId, body: data }: EditMealByProductVariables) =>
      cycleApi.request<MessageResponse>({
        ...FOOD_REQUEST_CONFIG.editMeal,
        url: `${FOOD_REQUEST_CONFIG.editMeal.url}/${mealId}/product`,
        data,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getCostTransactionChartKey.all() })
        await queryClient.invalidateQueries({ queryKey: getRecentFoodByProductKey.all() })
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateCreateProduct = () => {
  const cycleApi = useCycleApi()

  return useMutation(({ body: data }: CreateProductVariables) =>
    cycleApi.request<CreateProductMessageResponse>({ ...FOOD_REQUEST_CONFIG.createProduct, data })
  )
}

export const useMutateCreateNaturalProduct = () => {
  const cycleApi = useCycleApi()

  return useMutation(({ body: data }: CreateProductVariables) =>
    cycleApi.request<CreateProductMessageResponse>({
      ...FOOD_REQUEST_CONFIG.createNaturalProduct,
      data,
    })
  )
}

export const useMutateDeleteFood = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    (foodId: number) =>
      cycleApi.request({
        ...FOOD_REQUEST_CONFIG.deleteFood,
        url: `${FOOD_REQUEST_CONFIG.deleteFood.url}/${foodId}`,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getCostTransactionChartKey.all() })
        await queryClient.invalidateQueries({ queryKey: getRecentFoodByProductKey.all() })
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateDeleteFoodByProduct = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    (foodId: number) =>
      cycleApi.request({
        ...FOOD_REQUEST_CONFIG.deleteFood,
        url: `${FOOD_REQUEST_CONFIG.deleteFood.url}/${foodId}/product`,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getCostTransactionChartKey.all() })
        await queryClient.invalidateQueries({ queryKey: getRecentFoodByProductKey.all() })
        invalidateCostQueries()
      },
    }
  )
}
